import { zodResolver } from "@hookform/resolvers/zod";
import translateServerErrorIntoFriendlyMessage from "components/discussions/form/translateServerErrorIntoFriendlyMessage";
import type { EditPostTitleData } from "components/forum/form";
import { editPostTitleInputFormDataShape } from "components/forum/form";
import usePostActions from "components/forum/hooks/usePostActions";
import { Button } from "components/ui/Button";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { Input } from "components/ui/Input";
import classNames from "components/ui/classNames";
import PostRepliesQuery from "lib/graphql/queries/engagement/PostRepliesQuery";
import type { IPostRepliesQuery } from "lib/graphql/queries/engagement/__generated__/PostRepliesQuery.generated";
import { ButtonGroup, LinkButton } from "lib/miolo";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

interface IEditPostTitleFormProps {
  onCancel: () => void;
  topic: NonNullable<IPostRepliesQuery["postByShortId"]>;
}

export default function EditPostTitleForm({ topic, onCancel }: IEditPostTitleFormProps) {
  const { editPost } = usePostActions();
  const intl = useIntl();

  const form = useForm<EditPostTitleData>({
    resolver: zodResolver(editPostTitleInputFormDataShape(intl)),
    defaultValues: {
      title: topic.__typename === "ForumPost" ? topic.title : "",
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const handleSubmit = async ({ title }: EditPostTitleData) => {
    try {
      await editPost({
        variables: {
          objectId: topic.objectId,
          bodyAsJson: topic.bodyAsJson,
          bodyAsPlainText: topic.bodyAsPlainText,
          title,
        },
        refetchQueries: [
          {
            query: PostRepliesQuery,
            variables: {
              postShortId: topic.shortId,
              cursor: null,
            },
          },
        ],
      });

      setTimeout(() => {
        form.reset();
        onCancel();
      }, 1);
    } catch (err) {
      form.setError("root.serverError", {
        message: translateServerErrorIntoFriendlyMessage(
          err,
          intl,
          intl.formatMessage({
            defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
            id: "AsTfN9",
          }),
        ),
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-y-5">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <Input
                  {...field}
                  height={44}
                  type="text"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="Título"
                  name="title"
                  className="w-full"
                />

                <FormMessage />
              </div>
            );
          }}
        />

        <FormMessage asRootError={true} />

        <div className="flex w-full justify-end">
          <ButtonGroup stack="horizontally" spacing={20}>
            <LinkButton
              disabled={isSubmitting}
              variant="black"
              onClick={() => {
                form.reset();
                onCancel();
              }}
            >
              <FormattedMessage defaultMessage="Cancelar" id="nZLeaQ" />
            </LinkButton>

            <Button
              className={classNames(
                "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                {
                  "cursor-default bg-brand/50": isSubmitting,
                },
              )}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
              ) : (
                <FormattedMessage defaultMessage="Enviar" id="hKJZJR" />
              )}
            </Button>
          </ButtonGroup>
        </div>
      </form>
    </Form>
  );
}
